import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FaBookOpen,
  FaBuilding,
  FaCheckCircle,
  FaClipboardCheck,
  FaExclamationCircle,
  FaRedo,
  FaSearch,
  FaUserCheck,
  FaUserClock,
  FaUserPlus,
  FaUsers,
} from "react-icons/fa";
import { Users } from "../../components/Admins/Users";
import {
  getAdminBusinessOverview,
  getAdminBusinessUsers,
  getAdminDashboard,
} from "../../store/actions/adminActions";
import {
  BusinessAvatar,
  BusinessEmail,
  BusinessItem,
  BusinessItemTop,
  BusinessLayout,
  BusinessList,
  BusinessName,
  BusinessSummary,
  BusinessSkeleton,
  DirectoryCount,
  DirectoryHeader,
  DirectoryIcon,
  DirectorySearch,
  DirectoryTitleRow,
  DashboardNavRow,
  Eyebrow,
  Header,
  ItemStats,
  LearnerBusinessIcon,
  LearnerHeader,
  LearnerIdentity,
  Page,
  Panel,
  PanelMeta,
  PanelTitle,
  RetryButton,
  SearchInput,
  SelectedBadge,
  Skeleton,
  StatCard,
  StatIcon,
  StatLabel,
  StatsGrid,
  StatValue,
  State,
  Subtitle,
  SummaryCard,
  SummaryIcon,
  SummaryLabel,
  SummaryValue,
  Tab,
  TableLoadingBar,
  Tabs,
  Title,
} from "./AdminDashboardStyles";

const valueOf = (item, keys, fallback = 0) => {
  for (const key of keys) {
    if (item?.[key] !== undefined && item?.[key] !== null) return item[key];
  }
  return fallback;
};

const formatNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number.toLocaleString() : value ?? "—";
};

const businessName = (business) =>
  valueOf(business, ["orgName", "name", "business_name"], "Unnamed business");

const businessUserCount = (business) =>
  valueOf(business, ["users_count", "user_count", "total_users", "usersCount"]);

const businessTestCount = (business) =>
  valueOf(business, ["tests_count", "test_count", "completed_tests", "testsCount"]);

const BUSINESS_USERS_PAGE_SIZE = 5;

const OVERVIEW_STATS = (stats = {}, businessStats = {}, loadedBusinessCount) => [
  {
    label: "Total number of registered users",
    value: stats.total_number_of_registered_users,
    icon: <FaUsers />,
    color: "#2f67d8",
  },
  {
    label: "Total number of exams practised",
    value: stats.total_number_of_questions_practised,
    icon: <FaBookOpen />,
    color: "#7c3aed",
  },
  {
    label: "Total number of exams completed",
    value: stats.total_number_of_questions_finished,
    icon: <FaCheckCircle />,
    color: "#059669",
  },
  {
    label: "Total number of activated users",
    value: stats.total_number_of_users_activated,
    icon: <FaUserCheck />,
    color: "#0891b2",
  },
  {
    label: "Total number of users who have practised",
    value: stats.total_number_of_users_practised,
    icon: <FaClipboardCheck />,
    color: "#0f766e",
  },
  {
    label: "Total number of users who are yet to practise",
    value: stats.total_number_of_users_yet_practised,
    icon: <FaUserClock />,
    color: "#d97706",
  },
  {
    label: "Total number of users registered through referral",
    value: stats.total_number_of_referred_users,
    icon: <FaUserPlus />,
    color: "#db2777",
  },
  {
    label: "Total number of registered businesses",
    value:
      stats.total_number_of_registered_businesses ??
      stats.total_number_of_businesses ??
      businessStats.total_businesses ??
      loadedBusinessCount,
    icon: <FaBuilding />,
    color: "#4f46e5",
  },
];

export const AdminDashboard = ({ handle }) => {
  const dispatch = useDispatch();
  const { adminsdata } = useSelector((state) => state.admin);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedBusinessId, setSelectedBusinessId] = useState(null);
  const [businessSearch, setBusinessSearch] = useState("");
  const [usersPage, setUsersPage] = useState(1);

  const {
    businesses = [],
    businessOverviewStats = {},
    businessUsers = [],
    businessUsersMeta = {},
    businessesLoading,
    businessUsersLoading,
    businessesError,
    businessUsersError,
  } = adminsdata;

  useEffect(() => {
    dispatch(getAdminDashboard({}));
    dispatch(getAdminBusinessOverview({ pageSize: 100, currentPage: 1 }));
  }, [dispatch]);

  useEffect(() => {
    if (!selectedBusinessId && businesses.length) {
      setSelectedBusinessId(businesses[0].id);
    }
  }, [businesses, selectedBusinessId]);

  useEffect(() => {
    if (!selectedBusinessId) return;
    dispatch(
      getAdminBusinessUsers({
        businessId: selectedBusinessId,
        pageSize: BUSINESS_USERS_PAGE_SIZE,
        currentPage: usersPage,
        sort: "Newest",
      })
    );
  }, [dispatch, selectedBusinessId, usersPage]);

  const filteredBusinesses = useMemo(() => {
    const query = businessSearch.trim().toLowerCase();
    if (!query) return businesses;
    return businesses.filter((business) =>
      [businessName(business), business.email, business.country]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [businessSearch, businesses]);

  const selectedBusiness = businesses.find(
    (business) => String(business.id) === String(selectedBusinessId)
  );

  const totals = useMemo(() => {
    const listedUsers = businesses.reduce(
      (sum, business) => sum + Number(businessUserCount(business) || 0),
      0
    );
    const listedTests = businesses.reduce(
      (sum, business) => sum + Number(businessTestCount(business) || 0),
      0
    );
    return {
      businesses: valueOf(businessOverviewStats, ["total_businesses"], businesses.length),
      users: valueOf(
        businessOverviewStats,
        ["total_business_users", "total_users", "users_count"],
        listedUsers
      ),
      tests: valueOf(
        businessOverviewStats,
        ["total_tests_completed", "tests_count", "total_tests"],
        listedTests
      ),
    };
  }, [businessOverviewStats, businesses]);

  const selectBusiness = (id) => {
    if (String(id) === String(selectedBusinessId)) return;
    setUsersPage(1);
    setSelectedBusinessId(id);
  };

  const retryBusinesses = () => {
    dispatch(getAdminBusinessOverview({ pageSize: 100, currentPage: 1 }));
  };

  const retryUsers = () => {
    if (!selectedBusinessId) return;
    dispatch(
      getAdminBusinessUsers({
        businessId: selectedBusinessId,
        pageSize: BUSINESS_USERS_PAGE_SIZE,
        currentPage: usersPage,
        sort: "Newest",
      })
    );
  };

  return (
    <Page className="col-md-10 offset-md-1">
    

      <DashboardNavRow>
        <Tabs role="tablist" aria-label="Dashboard views">
          <Tab
            type="button"
            role="tab"
            aria-selected={activeTab === "overview"}
            $active={activeTab === "overview"}
            onClick={() => setActiveTab("overview")}
          >
            <FaClipboardCheck /> User Overview
          </Tab>
          <Tab
            type="button"
            role="tab"
            aria-selected={activeTab === "businesses"}
            $active={activeTab === "businesses"}
            onClick={() => setActiveTab("businesses")}
          >
            <FaBuilding /> Businesses
          </Tab>
        </Tabs>

       
      </DashboardNavRow>

      {activeTab === "overview" && (
        <StatsGrid role="tabpanel">
          {OVERVIEW_STATS(
            adminsdata.stats,
            businessOverviewStats,
            businesses.length || undefined
          ).map((stat, index) => (
            <StatCard key={stat.label} $color={stat.color} $index={index}>
              <StatIcon $color={stat.color}>{stat.icon}</StatIcon>
              <StatValue>{formatNumber(stat.value)}</StatValue>
              <StatLabel>{stat.label}</StatLabel>
            </StatCard>
          ))}
        </StatsGrid>
      )}

      {activeTab === "businesses" && (
        <div role="tabpanel">
          <BusinessLayout>
            <Panel>
               {activeTab === "businesses" && (
          <BusinessSummary $inline>
            <SummaryCard $tone="#f4f8ff" $accent="#2f67d8" $compact>
              <SummaryIcon $accent="#2f67d8"><FaBuilding /></SummaryIcon>
              <div>
                <SummaryValue>{formatNumber(totals.businesses)}</SummaryValue>
                <SummaryLabel>Registered businesses</SummaryLabel>
              </div>
            </SummaryCard>
            <SummaryCard $tone="#f5fbf8" $accent="#059669" $compact>
              <SummaryIcon $accent="#059669"><FaUsers /></SummaryIcon>
              <div>
                <SummaryValue>{formatNumber(totals.users)}</SummaryValue>
                <SummaryLabel>Business learners</SummaryLabel>
              </div>
            </SummaryCard>
            <SummaryCard $tone="#fbf7ff" $accent="#7c3aed" $compact>
              <SummaryIcon $accent="#7c3aed"><FaCheckCircle /></SummaryIcon>
              <div>
                <SummaryValue>{formatNumber(totals.tests)}</SummaryValue>
                <SummaryLabel>Tests completed</SummaryLabel>
              </div>
            </SummaryCard>
          </BusinessSummary>
        )}
              <DirectoryHeader>
                <DirectoryTitleRow>
                  {/* <DirectoryIcon><FaBuilding /></DirectoryIcon> */}
                  <div>
                    {/* <PanelTitle>
                      Business directory
                      <DirectoryCount>{formatNumber(filteredBusinesses.length)}</DirectoryCount>
                    </PanelTitle>
                    <PanelMeta>Choose a business to inspect its learner activity</PanelMeta> */}
                  </div>
                </DirectoryTitleRow>
                <DirectorySearch>
                  <FaSearch size={13} />
                  <SearchInput
                    value={businessSearch}
                    onChange={(event) => setBusinessSearch(event.target.value)}
                    placeholder="Search businesses"
                    aria-label="Search businesses"
                  />
                </DirectorySearch>
              </DirectoryHeader>

              {businessesLoading && !businesses.length ? (
                <BusinessList>{[1, 2, 3, 4].map((n) => <BusinessSkeleton key={n} $height="92px" />)}</BusinessList>
              ) : businessesError ? (
                <State $error>
                  <FaExclamationCircle size={24} />
                  <span>{businessesError}</span>
                  <RetryButton type="button" onClick={retryBusinesses}>
                    <FaRedo /> Try again
                  </RetryButton>
                </State>
              ) : filteredBusinesses.length ? (
                <BusinessList>
                  {filteredBusinesses.map((business) => {
                    const active = String(business.id) === String(selectedBusinessId);
                    return (
                      <BusinessItem
                        key={business.id}
                        type="button"
                        $active={active}
                        onClick={() => selectBusiness(business.id)}
                      >
                        <BusinessItemTop>
                          <BusinessAvatar $active={active}><FaBuilding /></BusinessAvatar>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <BusinessName>{businessName(business)}</BusinessName>
                            <BusinessEmail>{business.email || business.country || "Business account"}</BusinessEmail>
                          </div>
                        </BusinessItemTop>
                        <ItemStats>
                          <span><FaUsers /> {formatNumber(businessUserCount(business))} users</span>
                          <span><FaCheckCircle /> {formatNumber(businessTestCount(business))} tests</span>
                        </ItemStats>
                      </BusinessItem>
                    );
                  })}
                </BusinessList>
              ) : (
                <State><FaBuilding size={25} />No businesses found</State>
              )}
            </Panel>

            <Panel>
              <LearnerHeader>
                <LearnerIdentity>
                  <LearnerBusinessIcon><FaBuilding /></LearnerBusinessIcon>
                  <div>
                    <PanelTitle>{selectedBusiness ? businessName(selectedBusiness) : "Business learners"}</PanelTitle>
                    <PanelMeta>Learner status, units, school, attempts, completed tests, and location</PanelMeta>
                  </div>
                </LearnerIdentity>
                {selectedBusiness && (
                  <SelectedBadge>{formatNumber(businessUserCount(selectedBusiness))} learners</SelectedBadge>
                )}
              </LearnerHeader>

              {!selectedBusiness ? (
                <State><FaUsers size={25} />Select a business to view its learners</State>
              ) : businessUsersLoading && !businessUsers.length ? (
                <div>{[1, 2, 3, 4, 5].map((n) => <Skeleton key={n} $height="58px" />)}</div>
              ) : businessUsersError ? (
                <State $error>
                  <FaExclamationCircle size={24} />
                  <span>{businessUsersError}</span>
                  <RetryButton type="button" onClick={retryUsers}>
                    <FaRedo /> Try again
                  </RetryButton>
                </State>
              ) : (
                <>
                  {businessUsersLoading && <TableLoadingBar aria-label="Loading page" />}
                  <Users
                    data={businessUsers}
                    meta={businessUsersMeta}
                    currentPage={usersPage}
                    onGotoNextPage={() => setUsersPage((page) => page + 1)}
                    onGotoPrevPage={() => setUsersPage((page) => Math.max(1, page - 1))}
                    onGotoPage={setUsersPage}
                    handle={handle}
                    isBusiness
                    embedded
                  />
                </>
              )}
            </Panel>
          </BusinessLayout>
        </div>
      )}
    </Page>
  );
};
